import React, { ReactElement, ReactNode } from 'react'
import { Flex, ThemeUIStyleObject, Image } from 'theme-ui'
import { Button, Svg } from 'ui'
import { navigate } from 'gatsby'
import ButtonArrow from 'images/button-arrow-right.svg'
import SocialRatings from 'components/Organization/LandingPageCommonComponents/SocialRatings'
import { TextWithBreakpointVariant } from 'ui/TextWithBreakpointVariant'

export default function HeroSection({
  title,
  description,
  svg,
  image,
  sx,
  titleVariant,
  shouldUseBackground,
}: {
  title: string | ReactNode
  description?: string
  svg?: React.ElementType
  image?: string
  sx?: ThemeUIStyleObject
  titleVariant?: string[] | string
  shouldUseBackground?: boolean
}): ReactElement {
  const heroImageExists = svg || image

  return (
    <>
      {shouldUseBackground && (
        <Flex
          className="ios-scrolling-glitches-fix"
          sx={{
            position: 'absolute',
            top: '5rem',
            width: '100%',
            height: ['20rem', '25rem'],
            zIndex: 0,
            background: 'rosePink15',
            filter: 'blur(75px)',
          }}
        />
      )}
      <Flex
        sx={{
          width: '100%',
          mb: ['4.8rem', '10rem'],
          flexDirection: ['column', 'row'],
          alignItems: 'center',
          justifyContent: 'center',
          ...sx,
        }}
      >
        <Flex
          sx={{
            maxWidth: ['100%', '65rem'],
            width: '100%',
            flexDirection: 'column',
            gap: ['2.4rem', heroImageExists ? '4.8rem' : '4rem'],
            minWidth: ['auto', '40.8rem'],
            px: heroImageExists ? '3.2rem' : 0,
          }}
        >
          <Flex sx={{ flexDirection: 'column', gap: '1.6rem' }}>
            <TextWithBreakpointVariant
              variant={titleVariant || ['size32Weight500', 'size52Weight600']}
              sx={{
                color: 'aliceBlue',
              }}
              as="h1"
            >
              {title}
            </TextWithBreakpointVariant>
            {description && (
              <TextWithBreakpointVariant
                variant={['size14Weight500Line138Letter056', 'size16Weight500Line138Letter032']}
                sx={{ color: 'wildBlueYonder' }}
              >
                {description}
              </TextWithBreakpointVariant>
            )}
          </Flex>
          <Flex
            sx={{
              gap: ['0.8rem', '1.6rem'],
              alignContent: ['center', heroImageExists ? 'flex-start' : 'center'],
              flexDirection: 'row',
              flexWrap: ['wrap', 'nowrap'],
              justifyContent: ['center', heroImageExists ? 'flex-start' : 'center'],
            }}
          >
            <Button
              data-testid="take-it-now-button"
              sx={{
                display: 'inline-flex',
                justifyContent: 'center',
                flexShrink: 0,
                flex: ['1', 'none'],
                width: 'fit-content',
                minWidth: ['12.4rem', '18rem'],
                px: ['0.8rem', '2rem'],
                height: ['4rem', '4.7rem'],
                whiteSpace: 'nowrap',
                alignItems: 'center',
              }}
              variant="gradientButton"
              ariaLabel="Start Free Trial"
              name="Start Free Trial"
              onClick={() => navigate('/onboard')}
            >
              <TextWithBreakpointVariant
                variant={['size16Weight600Line138', 'size17Weight600Line138']}
              >
                {`Start Free Trial`}
              </TextWithBreakpointVariant>
              <Svg
                svg={ButtonArrow}
                sx={{
                  display: ['none', 'block'],
                  ml: '0.6rem',
                }}
                descriptiveText="Right arrow icon for Start Free Trial button"
              />
            </Button>
            <Button
              ariaLabel="Book a Demo"
              name="Book a Demo"
              data-testid="book-demo-button"
              sx={{
                display: 'inline-flex',
                flexShrink: 0,
                width: ['12.9rem', 'fit-content'],
                px: ['0.8rem', '2rem'],
                height: ['4rem', '4.7rem'],
                minWidth: ['12.9rem', '15rem'],
                whiteSpace: 'nowrap',
                '@media (max-width: 324px)': {
                  width: '100%',
                },
              }}
              variant="roundedSecondary"
              onClick={() => navigate('/demo')}
            >
              <TextWithBreakpointVariant
                variant={['size16Weight600Line138', 'size17Weight600Line138']}
              >
                Book a Demo
              </TextWithBreakpointVariant>
            </Button>
          </Flex>
          <SocialRatings
            sx={{
              justifyContent: heroImageExists ? ['center', 'flex-start'] : 'center',
            }}
          />
        </Flex>
        {heroImageExists && (
          <Flex
            sx={{
              justifyContent: 'center',
              mt: ['4.8rem', 0],
            }}
          >
            {/* setting the width to 63.6rem to make the image responsive */}
            {svg ? (
              <Svg
                svg={svg}
                sx={{ flexShrink: 1, width: ['100%', '100%', '63.6rem'], height: '100%' }}
                descriptiveText={`Hero section illustration for ${title}`}
              />
            ) : (
              <Image
                src={image}
                aria-label={`Hero section illustration for ${title}`}
                sx={{
                  flexShrink: 1,
                  width: ['100%', '100%', '63.6rem'],
                  height: '100%',
                  '@media screen and (min-width: 768px) and (max-width: 980px)': {
                    width: '57rem',
                  },
                }}
              />
            )}
          </Flex>
        )}
      </Flex>
    </>
  )
}
